import React from 'react';

const UPLOADS = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Détermine si une couleur hex est sombre (pour choisir texte blanc ou noir)
function isColorDark(hex) {
  if (!hex || !hex.startsWith('#')) return true;
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  // Luminance relative
  return (r*299 + g*587 + b*114) / 1000 < 128;
}

function sectionBg(theme, settings, defaultDark = true) {
  if (!theme) return defaultDark ? (settings['color.primary']||'#0A1628') : '#ffffff';
  if (theme.startsWith('#')) return theme;
  if (theme === 'dark')  return settings['color.primary'] || '#0A1628';
  if (theme === 'blue')  return '#0D3B6E';
  return '#ffffff';
}

// ── Impact ────────────────────────────────────────────────────────────────────
export function Impact({ content, settings = {} }) {
  const theme  = settings['theme.impact'] || 'dark';
  const bg     = theme.startsWith('#') ? theme
               : theme==='light' ? '#fff'
               : theme==='blue'  ? '#0D3B6E'
               : (settings['color.primary']||'#0A1628');
  const isDark = theme === 'light' ? false : theme.startsWith('#') ? isColorDark(theme) : true;

  const stats = [
    { num:settings['impact.stat1.num']||'4,5 Mds',  label:settings['impact.stat1.label']||'FCFA mobilisés pour le logement abordable' },
    { num:settings['impact.stat2.num']||'~450',      label:settings['impact.stat2.label']||'Ménages bénéficiaires de logements accessibles' },
    { num:settings['impact.stat3.num']||'12 ans',    label:settings['impact.stat3.label']||"Durée d'engagement du programme (2026–2038)" },
    { num:settings['impact.stat4.num']||'UEMOA',     label:settings['impact.stat4.label']||"Autorisé et régulé par l'AMF-UMOA" },
  ];

  return (
    <section id="impact" style={{ padding:'4rem 0', background:bg, position:'relative', overflow:'hidden' }}>
      <style>{`
        .impact-grid { display:grid; grid-template-columns:1fr; gap:2.5rem; margin-top:1.5rem; }
        .impact-stats { display:grid; grid-template-columns:repeat(2,1fr); gap:1rem; }
        @media(min-width:900px){ .impact-grid{ grid-template-columns:1fr 1fr; gap:4rem; } }
      `}</style>
      <div style={{ position:'absolute', bottom:0, right:0, width:300, height:300, background:'radial-gradient(circle,rgba(200,152,42,0.08),transparent 65%)', pointerEvents:'none' }} />
      <div className="section-inner">
        <div className="section-label" style={{ color:isDark?'var(--color-accent-light)':'var(--color-accent)' }}>Impact social & financier</div>
        <div className="impact-grid">
          <div>
            <h2 className="section-title" style={{ color:isDark?'#fff':undefined }}>{content.title||'Financer le logement, transformer des vies'}</h2>
            <p style={{ fontSize:15, color:isDark?'rgba(255,255,255,0.5)':'var(--color-text-mid)', lineHeight:1.8, marginBottom:'1.5rem' }}>{content.description||"Ce programme mobilise l'épargne pour financer des logements abordables en Côte d'Ivoire."}</p>
            {content.quote && (
              <blockquote style={{ fontSize:'1.1rem', fontFamily:"'Playfair Display',serif", fontStyle:'italic', color:isDark?'rgba(255,255,255,0.85)':'var(--color-text)', lineHeight:1.6, borderLeft:'3px solid var(--color-accent)', paddingLeft:'1.5rem', marginBottom:'1rem' }}>
                "{content.quote}"
              </blockquote>
            )}
            <div style={{ fontSize:11, color:isDark?'rgba(255,255,255,0.3)':'var(--color-text-light)', letterSpacing:1, textTransform:'uppercase', paddingLeft:'1.5rem' }}>— Programme ZAKA · AFINHAB & BOAD Titrisation</div>
          </div>
          <div className="impact-stats">
            {stats.map((s,i) => (
              <div key={i} style={{ background:isDark?'rgba(255,255,255,0.04)':'var(--color-bg)', border:`1px solid ${isDark?'rgba(255,255,255,0.07)':'var(--color-border)'}`, borderRadius:6, padding:'1.2rem' }}>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:'1.8rem', fontWeight:700, color:'var(--color-accent)', marginBottom:'0.3rem' }}>{s.num}</div>
                <div style={{ fontSize:12, color:isDark?'rgba(255,255,255,0.5)':'var(--color-text-mid)', lineHeight:1.5 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Documents ─────────────────────────────────────────────────────────────────
export function Documents({ documents, content, settings = {}, showToast }) {
  const theme  = settings['theme.documents'] || 'light';
  const bg     = theme.startsWith('#') ? theme
               : theme==='dark'  ? (settings['color.primary']||'#0A1628')
               : theme==='blue'  ? '#0D3B6E'
               : '#ffffff';
  const isDark = theme === 'light' ? false : theme.startsWith('#') ? isColorDark(theme) : true;
  const fmtSize = b => b ? (b>1048576 ? `${(b/1048576).toFixed(1)} Mo` : `${Math.round(b/1024)} Ko`) : '';
  const icons  = { 'application/pdf':'📄', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':'📝', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':'📊' };

  return (
    <section id="documents" style={{ padding:'4rem 0', background:bg }}>
      <div className="section-inner">
        <div className="section-label">Documentation officielle</div>
        <h2 className="section-title" style={{ color:isDark?'#fff':undefined }}>{content.title||'Télécharger les documents'}</h2>
        <p className="section-desc" style={{ marginBottom:'2rem', color:isDark?'rgba(255,255,255,0.6)':undefined }}>{content.subtitle||''}</p>
        {!documents.length && (
          <div style={{ color:isDark?'rgba(255,255,255,0.3)':'var(--color-text-mid)', fontSize:14, padding:'2rem', textAlign:'center', border:`1px dashed ${isDark?'rgba(255,255,255,0.15)':'var(--color-border)'}`, borderRadius:6 }}>
            Aucun document disponible.
          </div>
        )}
        <div style={{ display:'flex', flexDirection:'column', gap:'0.8rem' }}>
          {documents.map(doc => (
            <div key={doc.id} style={{ display:'flex', alignItems:'center', gap:'0.8rem', padding:'0.9rem 1rem', border:`1px solid ${isDark?'rgba(255,255,255,0.08)':'var(--color-border)'}`, borderRadius:6, background:isDark?'rgba(255,255,255,0.04)':'#fff', flexWrap:'wrap' }}>
              <div style={{ width:36, height:36, background:isDark?'rgba(255,255,255,0.08)':'#FFF3E0', borderRadius:4, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>
                {icons[doc.mime_type]||'📁'}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:isDark?'#fff':'var(--color-text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{doc.name}</div>
                <div style={{ fontSize:11, color:isDark?'rgba(255,255,255,0.35)':'var(--color-text-light)', marginTop:2 }}>
                  {doc.description && `${doc.description} · `}{fmtSize(doc.file_size)}
                </div>
              </div>
              <a href={`${UPLOADS}/api/documents/${doc.id}/download`} download
                style={{ background:'var(--color-primary)', color:'white', border:'none', padding:'6px 12px', borderRadius:3, fontSize:11, cursor:'pointer', textDecoration:'none', flexShrink:0 }}>
                ↓ Télécharger
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Contact (anciennement Souscription) ───────────────────────────────────────
export function Souscription({ settings, showToast }) {
  const [form, setForm]   = React.useState({ nom:'', email:'', tel:'', sujet:'', message:'' });
  const [sending, setSending] = React.useState(false);

  const inp = { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:4, padding:'10px 14px', color:'white', fontFamily:"'DM Sans',sans-serif", fontSize:14, outline:'none', width:'100%' };

  const handleSubmit = async () => {
    if (!form.nom || !form.email || !form.message) { showToast('⚠ Nom, email et message obligatoires'); return; }
    setSending(true);
    try {
      const r = await fetch(`${UPLOADS}/api/contact`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      showToast('✓ Message envoyé ! Nous vous répondrons rapidement.');
      setForm({ nom:'', email:'', tel:'', sujet:'', message:'' });
    } catch(e) { showToast(`✗ ${e.message||"Erreur d'envoi"}`); }
    finally { setSending(false); }
  };

  return (
    <section id="contact" style={{ padding:'4rem 0', background: sectionBg(settings['theme.contact'], settings, true) }}>
      <style>{`
        .contact-grid { display:grid; grid-template-columns:1fr; gap:2.5rem; margin-top:1.5rem; }
        @media(min-width:768px){ .contact-grid{ grid-template-columns:1fr 1fr; gap:3rem; align-items:start; } }
        .form-row-2 { display:grid; grid-template-columns:1fr; gap:1rem; }
        @media(min-width:480px){ .form-row-2{ grid-template-columns:1fr 1fr; } }
      `}</style>
      <div className="section-inner">
        <div className="section-label" style={{ color:'var(--color-accent-light)' }}>Nous contacter</div>
        <div className="contact-grid">
          {/* Infos */}
          <div>
            <h2 className="section-title light">Une question ?<br/>Écrivez-nous</h2>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.5)', lineHeight:1.8, marginBottom:'1.5rem' }}>
              Notre équipe est disponible pour répondre à toutes vos questions sur le programme ZAKA FCTC RMBS 2026–2038.
            </p>
            {[['Site web', settings['contact.website']||'www.zaka.org'],['Email', settings['contact.email']||'contact@zaka.org'],['Souscription','15 → 22 juin 2026']].map(([l,v]) => (
              <div key={l} style={{ background:'rgba(200,152,42,0.08)', border:'1px solid rgba(200,152,42,0.2)', borderRadius:6, padding:'0.9rem 1.2rem', marginBottom:'0.8rem' }}>
                <div style={{ fontSize:9, letterSpacing:2, textTransform:'uppercase', color:'var(--color-accent)', marginBottom:3 }}>{l}</div>
                <div style={{ fontSize:14, color:'white', fontWeight:500 }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Formulaire */}
          <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(200,152,42,0.2)', borderRadius:8, padding:'1.8rem' }}>
            <div className="form-row-2" style={{ marginBottom:'1rem' }}>
              <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
                <label style={{ fontSize:10, letterSpacing:1, textTransform:'uppercase', color:'rgba(255,255,255,0.5)' }}>Nom complet *</label>
                <input style={inp} value={form.nom} onChange={e=>setForm({...form,nom:e.target.value})} placeholder="Jean Kouassi" />
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
                <label style={{ fontSize:10, letterSpacing:1, textTransform:'uppercase', color:'rgba(255,255,255,0.5)' }}>Téléphone</label>
                <input style={inp} type="tel" value={form.tel} onChange={e=>setForm({...form,tel:e.target.value})} placeholder="+225 07 00 00 00" />
              </div>
            </div>
            <div style={{ marginBottom:'1rem', display:'flex', flexDirection:'column', gap:'0.4rem' }}>
              <label style={{ fontSize:10, letterSpacing:1, textTransform:'uppercase', color:'rgba(255,255,255,0.5)' }}>Email *</label>
              <input style={inp} type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="jean@exemple.com" />
            </div>
            <div style={{ marginBottom:'1rem', display:'flex', flexDirection:'column', gap:'0.4rem' }}>
              <label style={{ fontSize:10, letterSpacing:1, textTransform:'uppercase', color:'rgba(255,255,255,0.5)' }}>Sujet</label>
              <input style={inp} value={form.sujet} onChange={e=>setForm({...form,sujet:e.target.value})} placeholder="Ex : Renseignement Tranche Senior" />
            </div>
            <div style={{ marginBottom:'1.2rem', display:'flex', flexDirection:'column', gap:'0.4rem' }}>
              <label style={{ fontSize:10, letterSpacing:1, textTransform:'uppercase', color:'rgba(255,255,255,0.5)' }}>Message *</label>
              <textarea style={{ ...inp, resize:'vertical', minHeight:100 }} value={form.message} onChange={e=>setForm({...form,message:e.target.value})} placeholder="Votre message..." />
            </div>
            <button onClick={handleSubmit} disabled={sending}
              style={{ width:'100%', background:'var(--color-accent)', color:'var(--color-primary)', border:'none', borderRadius:3, padding:13, fontFamily:"'Playfair Display',serif", fontSize:15, fontWeight:700, cursor:sending?'not-allowed':'pointer', opacity:sending?0.7:1 }}>
              {sending ? 'Envoi...' : 'Envoyer le message'}
            </button>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.2)', marginTop:'1rem', lineHeight:1.7 }}>
              Vos coordonnées sont utilisées uniquement pour répondre à votre demande.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
export function Footer({ settings, content }) {
  const UPLOADS    = process.env.REACT_APP_API_URL || 'http://localhost:4000';
  const copyright  = settings['footer.copyright']  || '';
  const link1      = settings['footer.link1']       || '';
  const link2      = settings['footer.link2']       || '';
  const logoPath   = settings['footer.logo']        ? `${UPLOADS}${settings['footer.logo']}` : null;
  const logoAlt    = settings['logo.alt']           || 'ZAKA';
  const logoColor  = settings['footer.logo_color']  || 'var(--color-accent)';

  const links = [link1, link2, settings['contact.website']||'', 'Contact'].filter(Boolean);

  return (
    <footer style={{ background:'#060E1C', padding:'2.5rem 0' }}>
      <style>{`
        .footer-inner { max-width:1200px; margin:0 auto; padding:0 1.2rem; }
        .footer-top { display:flex; flex-direction:column; gap:1.2rem; padding-bottom:1.5rem; border-bottom:1px solid rgba(255,255,255,0.06); }
        .footer-links { display:flex; flex-wrap:wrap; gap:1rem 1.5rem; }
        .footer-bottom { display:flex; flex-direction:column; gap:0.8rem; padding-top:1.2rem; }
        @media(min-width:640px){
          .footer-top { flex-direction:row; justify-content:space-between; align-items:center; }
          .footer-bottom { flex-direction:row; justify-content:space-between; align-items:center; }
        }
      `}</style>
      <div className="footer-inner">
        <div className="footer-top">
          {/* Logo ou texte */}
          {logoPath
            ? <img src={logoPath} alt={logoAlt} style={{ height:36, maxWidth:160, objectFit:'contain' }} />
            : <span style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:900, color: logoColor, letterSpacing:2 }}>
                {logoAlt}
              </span>
          }
          <div className="footer-links">
            {links.map((l, i) => (
              <a key={i} href="#" style={{ color:'rgba(255,255,255,0.4)', fontSize:12, textDecoration:'none' }}>{l}</a>
            ))}
          </div>
        </div>
        <div className="footer-bottom">
          <div style={{ fontSize:11, color:'rgba(255,255,255,0.2)' }}>{copyright}</div>
          <div style={{ fontSize:11, color:'rgba(255,255,255,0.2)', lineHeight:1.6, maxWidth:400 }}>
            {content.legal || ''}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────
export function Toast({ message }) {
  if (!message) return null;
  return (
    <div style={{ position:'fixed', bottom:'1.5rem', right:'1rem', left:'1rem', background:'var(--color-primary)', border:'1px solid var(--color-accent)', borderRadius:6, padding:'1rem 1.2rem', color:'white', fontSize:14, zIndex:9999, textAlign:'center' }}>
      {message}
    </div>
  );
}
